import React, { useState } from 'react';

import Grid from '@mui/material/Grid2';

import {
  CreatureLimitation,
  DiceRoll,
  ItemLimitation,
  LOOT_ITEM_SOURCES,
  LootEntry,
  LootItemGroup,
  LootItemSource,
} from "@dndModels";
import { CREATURE_CRS } from "@dndModels/Constants";
import { ENCOUNTER_DIFFICULTIES } from "@dndUtilities/encounter_generator";

import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import CreatureLimitInput from "@dndComponents/limitations/CreatureLimitInput";
import ItemLimitInput from "@dndComponents/limitations/ItemLimitInput";
import CheckBox from "@components/input/CheckBox";
import DandDButtonBox from '@dndComponents/DandDButtonBoxSimple';
import DeleteButton from "@components/input/buttons/icon_buttons/DeleteButton";

interface Props {
  entry: LootEntry;
  disabled: boolean;
  item_options: { id: string, name: string }[]; // Every item, for picking Specific Items
  onChange: (update: (entry: LootEntry) => void) => void;
}

const CR_OPTIONS = ["Any", ...CREATURE_CRS.filter(o => +o <= 30)];

// Count and size of a DiceRoll, where a size of 1 means the count is a flat number.
function DiceRollFields({ name, dice, disabled, onChange }: { 
  name: string, 
  dice: DiceRoll, 
  disabled: boolean, 
  onChange: (update: (dice: DiceRoll) => void) => void 
}) {
  return (
    <Grid container spacing={0} direction="row">
      <Grid size={6}>
        <StringBox
          name={`${name} Count`}
          type="number"
          value={dice.count}
          disabled={disabled}
          onBlur={(changed: string) => onChange(o => { o.count = +changed; })}
        />
      </Grid>
      <Grid size={6}>
        <StringBox
          name={`${name} Die Size (1 = flat)`}
          type="number"
          value={dice.size}
          disabled={disabled}
          onBlur={(changed: string) => onChange(o => { o.size = Math.max(+changed, 1); })}
        />
      </Grid>
    </Grid>
  );
}

// Searches the items by name to pick one.
function SpecificItemPicker({ item_id, item_options, disabled, onChange }: {
  item_id: string,
  item_options: { id: string, name: string }[],
  disabled: boolean,
  onChange: (item_id: string) => void
}) {
  const [search, setSearch] = useState("");
  const current = item_options.find(o => o.id === item_id);
  const matches = search.length < 2 ? [] :
    item_options.filter(o => o.name.toLowerCase().includes(search.toLowerCase())).slice(0, 15);
  return (
    <Grid container spacing={0} direction="row">
      <Grid size={12}>
        <b>Item:</b> { current ? current.name : (item_id === "" ? "None" : `Not found (${item_id})`) }
      </Grid>
      <Grid size={12}>
        <StringBox
          name="Search Items"
          value={search}
          disabled={disabled}
          onChange={(changed: string) => setSearch(changed)}
        />
      </Grid>
      { matches.map(o => (
        <Grid key={o.id} size={12} className="clickable padded" onClick={() => {
          onChange(o.id);
          setSearch("");
        }}>
          { o.name }
        </Grid>
      ))}
    </Grid>
  );
}

const valueString = (value: number) => value < 0 ? "" : `${value}`;
const parseValue = (changed: string) => changed.trim() === "" ? -1 : Math.max(+changed, 0);

export default function LootEntryInput({
  entry,
  disabled,
  item_options,
  onChange,
}: Props) {
  const updateGroup = (true_id: string, update: (group: LootItemGroup) => void) => {
    onChange(o => {
      const group = o.item_groups.find(g => g.true_id === true_id);
      if (group) {
        update(group);
      }
    });
  };

  const renderGroup = (group: LootItemGroup, index: number) => {
    return (
      <Grid key={group.true_id} size={12} className="padded bordered">
        <Grid container spacing={1} direction="row">
          <Grid size={5} className="header">
            Item Group { index + 1 }
          </Grid>
          <Grid size={6}>
            <SelectStringBox
              name="Gives"
              options={[...LOOT_ITEM_SOURCES]}
              value={group.source}
              auto_sort={false}
              disabled={disabled}
              onChange={(changed: string) => updateGroup(group.true_id, g => { g.source = changed as LootItemSource; })}
            />
          </Grid>
          <Grid size={1} className="centered">
            <DeleteButton
              disabled={disabled}
              no_fab
              onClick={() => onChange(o => { o.item_groups = o.item_groups.filter(g => g.true_id !== group.true_id); })}
            />
          </Grid>
          <Grid size={12}>
            <DiceRollFields name={group.source === "Loot Table" ? "Rolls" : "Item"} dice={group.count} disabled={disabled}
              onChange={update => updateGroup(group.true_id, g => update(g.count))}
            />
          </Grid>
          { group.source === "Items" &&
            <>
              <Grid size={6}>
                <StringBox
                  name="Min Value in gp (empty for none)"
                  type="number"
                  value={valueString(group.min_value)}
                  disabled={disabled}
                  onBlur={(changed: string) => updateGroup(group.true_id, g => { g.min_value = parseValue(changed); })}
                />
              </Grid>
              <Grid size={6}>
                <StringBox
                  name="Max Value in gp (empty for none)"
                  type="number"
                  value={valueString(group.max_value)}
                  disabled={disabled}
                  onBlur={(changed: string) => updateGroup(group.true_id, g => { g.max_value = parseValue(changed); })}
                />
              </Grid>
              <Grid size={12} className="small_label gray">
                The value range is for things like gems and art objects. Magic items count as 0 gp.
              </Grid>
              <Grid size={12}>
                <ItemLimitInput
                  obj={group.item_limit}
                  disabled={disabled}
                  onChange={(changed: ItemLimitation) => updateGroup(group.true_id, g => { g.item_limit = changed; })}
                />
              </Grid>
            </>
          }
          { group.source === "Specific Item" &&
            <Grid size={12}>
              <SpecificItemPicker
                item_id={group.item_id}
                item_options={item_options}
                disabled={disabled}
                onChange={(changed: string) => updateGroup(group.true_id, g => { g.item_id = changed; })}
              />
            </Grid>
          }
          { group.source === "Loot Table" &&
            <>
              <Grid size={12}>
                <SelectObjectBox
                  name="Loot Table"
                  data_type="loot_table"
                  value={group.loot_table_id}
                  disabled={disabled}
                  onChange={(changed: string) => updateGroup(group.true_id, g => { g.loot_table_id = changed; })}
                />
              </Grid>
              <Grid size={12} className="small_label gray">
                Rolls pick that table&apos;s entries by weight alone, ignoring its difficulty, CR, Environment, and creature settings.
              </Grid>
            </>
          }
        </Grid>
      </Grid>
    );
  };

  return (
    <Grid container spacing={1} direction="row">
      <Grid size={8}>
        <StringBox
          name="Name"
          value={entry.name}
          disabled={disabled}
          onBlur={(changed: string) => onChange(o => { o.name = changed; })}
        />
      </Grid>
      <Grid size={4}>
        <StringBox
          name="Weight"
          type="number"
          value={entry.weight}
          disabled={disabled}
          onBlur={(changed: string) => onChange(o => { o.weight = Math.max(+changed, 0); })}
        />
      </Grid>
      <Grid size={12}>
        <CheckBox
          name="Guaranteed (given once whenever it fits, instead of being rolled for)"
          value={entry.guaranteed}
          disabled={disabled}
          onChange={(changed: boolean) => onChange(o => { o.guaranteed = changed; })}
        />
      </Grid>
      <Grid size={12} className="header">
        When It Can Drop
      </Grid>
      <Grid size={6}>
        <SelectStringBox
          name="Encounter Difficulties (empty for any)"
          options={[...ENCOUNTER_DIFFICULTIES]}
          values={entry.difficulties}
          multiple
          auto_sort={false}
          disabled={disabled}
          onChange={(changed: string[]) => onChange(o => { o.difficulties = changed; })}
        />
      </Grid>
      <Grid size={3}>
        <SelectStringBox
          name="Min Highest CR"
          options={CR_OPTIONS}
          value={entry.min_cr < 0 ? "Any" : `${entry.min_cr}`}
          auto_sort={false}
          disabled={disabled}
          onChange={(changed: string) => onChange(o => { o.min_cr = changed === "Any" ? -1 : +changed; })}
        />
      </Grid>
      <Grid size={3}>
        <SelectStringBox
          name="Max Highest CR"
          options={CR_OPTIONS}
          value={entry.max_cr < 0 ? "Any" : `${entry.max_cr}`}
          auto_sort={false}
          disabled={disabled}
          onChange={(changed: string) => onChange(o => { o.max_cr = changed === "Any" ? -1 : +changed; })}
        />
      </Grid>
      <Grid size={12}>
        <SelectObjectBox
          name="Environments (empty for anywhere)"
          data_type="environment"
          values={entry.environment_ids}
          multiple
          disabled={disabled}
          onChange={(changed: string[]) => onChange(o => { o.environment_ids = changed; })}
        />
      </Grid>
      <Grid size={12} className="small_label gray">
        Creatures it drops from. At least one creature in the encounter has to fit, unless this is left empty.
      </Grid>
      <Grid size={12}>
        <CreatureLimitInput
          obj={entry.creature_limit}
          disabled={disabled}
          ignore_knowledge
          onChange={(changed: CreatureLimitation) => onChange(o => { o.creature_limit = changed; })}
        />
      </Grid>
      <Grid size={12} className="header">
        What It Gives
      </Grid>
      { entry.item_groups.map(renderGroup) }
      <Grid size={12}>
        <DandDButtonBox
          name="Add Item Group"
          disabled={disabled}
          onClick={() => onChange(o => { o.item_groups.push(new LootItemGroup()); })}
        />
      </Grid>
      { (["pp","gp","ep","sp","cp"] as const).map(coin => (
        <Grid key={coin} size={12}>
          <DiceRollFields name={coin.toUpperCase()} dice={entry.money[coin]} disabled={disabled} 
            onChange={update => onChange(o => update(o.money[coin]))} 
          />
        </Grid>
      ))}
    </Grid>
  );
}
